import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert } from "react-bootstrap";
import { gymMembersAPI } from "../../api/gymMembers";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import { FaUserPlus, FaArrowLeft } from "react-icons/fa";

const AddMember = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    emergency_number: "",
    email: "",
    address: "",
    medical_history: "",
    dob: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dob: date,
    });
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.first_name.trim()) errors.push("First name is required");
    if (!formData.last_name.trim()) errors.push("Last name is required");

    if (!formData.mobile_number.trim()) {
      errors.push("Mobile number is required");
    } else if (!/^\d{10}$/.test(formData.mobile_number)) {
      errors.push("Mobile number must be 10 digits");
    }

    if (!formData.email.trim()) errors.push("Email is required");
    if (!/^\S+@\S+\.\S+$/.test(formData.email))
      errors.push("Invalid email format");

    if (!formData.dob) errors.push("Date of birth is required");
    if (formData.dob && new Date(formData.dob) > new Date()) {
      errors.push("Date of birth cannot be in the future");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: errors.map((err) => `<p>${err}</p>`).join(""),
      });
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Format dates for API (YYYY-MM-DD)
      const formattedData = {
        ...formData,
        dob: formData.dob ? formData.dob.toISOString().split("T")[0] : null,
      };

      // Remove null/undefined fields
      Object.keys(formattedData).forEach((key) => {
        if (
          formattedData[key] === null ||
          formattedData[key] === undefined ||
          formattedData[key] === ""
        ) {
          delete formattedData[key];
        }
      });

      const response = await gymMembersAPI.createMember(formattedData);

      Swal.fire({
        icon: "success",
        title: "Member Created Successfully!",
        text: `Member ID: ${response.data.member_id}`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        mobile_number: "",
        emergency_number: "",
        email: "",
        address: "",
        medical_history: "",
        dob: null,
      });

      // Navigate to members list
      setTimeout(() => navigate("/members"), 2000);
    } catch (error) {
      console.error("Error creating member:", error);
      setError(error.response?.data?.message || "Failed to create member");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to create member",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-member-page">
      <div className="page-header mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/members")}
          className="me-3"
        >
          <FaArrowLeft /> Back
        </Button>
        <h2 className="mb-0 d-flex align-items-center">
          <FaUserPlus className="me-2" /> Add New Member
        </h2>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    Personal Information
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            placeholder="Enter first name"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            placeholder="Enter last name"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth *</Form.Label>
                      <DatePicker
                        selected={formData.dob}
                        onChange={handleDateChange}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select date of birth"
                        maxDate={new Date()}
                        showYearDropdown
                        dropdownMode="select"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile_number"
                        value={formData.mobile_number}
                        onChange={handleChange}
                        placeholder="9876543210"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        required
                      />
                      <Form.Text className="text-muted">
                        10 digits required
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Emergency Contact Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="emergency_number"
                        value={formData.emergency_number}
                        onChange={handleChange}
                        placeholder="9876500000"
                        pattern="[0-9]{10}"
                        maxLength="10"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="member@example.com"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter address"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-success text-white">
                    Medical Information
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Medical History</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={12}
                        name="medical_history"
                        value={formData.medical_history}
                        onChange={handleChange}
                        placeholder="Any medical conditions, allergies, or special requirements..."
                      />
                      <Form.Text className="text-muted">
                        Enter "None" if no medical history
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-4">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="px-5"
              >
                {loading ? "Creating Member..." : "Create Member"}
              </Button>

              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => navigate("/members")}
                className="ms-3"
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddMember;
